import React, { useState, useCallback, useContext, useEffect } from "react";
import { GlobalContext } from "../GlobalContext";

const LANG_NAME = { kr: "Korean", en: "English", ru: "Russian" };
const GT_CODE = { kr: "ko", en: "en", ru: "ru" };
// Value slots in a phrasing: a bare "$" or a numbered "$1" (not "$s1"-style variables).
const countSlots = (s) => (String(s || "").match(/\$\d*(?!\w)/g) || []).length;

/**
 * The "Untranslated terms" list shared by the Batch Translator and the Workbench
 * scan: strings the auto-translator could not match, most frequent first, each
 * with quick English / Russian inputs that save straight to the term database
 * (same upsert path as the Translation Helper, so the server cache is flushed).
 *
 * props:
 *   missing      [{key, sample, lang, count, hint, hintLine}]
 *   resetKey     changes whenever `missing` comes from a new run (saved marks are
 *                dropped; text typed but not saved yet is kept)
 *   notify       (kind, message) => void   toast helper of the host page
 *   onRerun      optional: run again with the freshly saved terms
 *   rerunLabel / rerunTitle / rerunDisabled
 *   onPick       optional: extra per-row action (e.g. "Find" in the Workbench)
 *   pickLabel / pickTitle
 */
export default function MissingTerms({
  missing,
  resetKey,
  notify,
  onRerun,
  rerunLabel = "Re-run translation",
  rerunTitle = "Translate again using the terms you just saved",
  rerunDisabled = false,
  onPick,
  pickLabel = "Pick",
  pickTitle = "",
}) {
  const { socket, isSocketReady } = useContext(GlobalContext);
  const [edits, setEdits] = useState({}); // key -> {en, ru, saving, saved, error}
  const [filter, setFilter] = useState("");

  // A new run: forget what was saved (those strings are gone from the list), but
  // keep anything typed and not saved yet.
  useEffect(() => {
    setEdits((prev) => {
      const kept = {};
      for (const [k, v] of Object.entries(prev)) {
        if (!v.saved && (v.en || v.ru)) kept[k] = { en: v.en, ru: v.ru };
      }
      return kept;
    });
  }, [resetKey]);

  // Promise wrapper over an ack-based socket event, with a timeout.
  const emitAck = useCallback(
    (event, payload, timeoutMs = 20000) =>
      new Promise((resolve) => {
        if (!socket || !isSocketReady) {
          resolve({ error: "Server offline." });
          return;
        }
        let done = false;
        const timer = setTimeout(() => {
          if (!done) {
            done = true;
            resolve({ error: "Timed out waiting for the server." });
          }
        }, timeoutMs);
        socket.emit(event, payload, (resp) => {
          if (done) return;
          done = true;
          clearTimeout(timer);
          resolve(resp || { error: "No response from the server." });
        });
      }),
    [socket, isSocketReady]
  );

  const say = (kind, msg) => {
    if (notify) notify(kind, msg);
  };
  const setEdit = (key, patch) =>
    setEdits((prev) => ({ ...prev, [key]: { ...(prev[key] || {}), ...patch } }));

  // English is always the `term`; other languages live under it. A source
  // phrasing whose English already exists joins that term as an alternate.
  const save = async (m) => {
    const ed = edits[m.key] || {};
    const en = (ed.en || "").trim();
    const ru = (ed.ru || "").trim();
    // A "$" in the translation is only ever filled from a "$" slot of the source.
    // A value at the very END of a line has no slot (it is re-attached on its
    // own), so "최대 HP +40" is the term "최대 HP" -> "Max HP", without a "$".
    const srcSlots = countSlots(m.key);
    for (const [label, val] of [["English", en], ["Russian", ru]]) {
      const n = countSlots(val);
      if (val && n > srcSlots) {
        const msg = `The ${label} text has ${n} "$" but the source has ${srcSlots}. A value at the end of a line is added automatically - leave its "$" out.`;
        setEdit(m.key, { error: msg });
        say("error", msg);
        return;
      }
    }
    const ops = [];
    if (m.lang === "en") {
      if (!ru) {
        say("error", "Enter the Russian translation.");
        return;
      }
      ops.push({ src: m.key, term: ru, language: "English", resultLang: "Russian" });
    } else {
      if (!en) {
        say("error", "Enter the English term.");
        return;
      }
      ops.push({ src: m.key, term: en, language: LANG_NAME[m.lang] || "Korean", resultLang: "English" });
      if (ru && m.lang !== "ru") ops.push({ src: en, term: ru, language: "English", resultLang: "Russian" });
    }
    setEdit(m.key, { saving: true, error: null });
    let variant = false;
    for (const op of ops) {
      const resp = await emitAck("copyTerm", op);
      if (!resp || resp.ok === false || resp.error) {
        setEdit(m.key, { saving: false, error: (resp && resp.error) || "Save failed." });
        say("error", "Could not save the term.");
        return;
      }
      if (resp.variant) variant = true;
    }
    setEdit(m.key, { saving: false, saved: true });
    say(
      "success",
      variant
        ? "Saved as an alternate phrasing of an existing term - cache flushed."
        : "Term saved - cache flushed."
    );
  };

  // Prefill from Google Translate using the raw sample (real numbers translate
  // better than "$"); the user then puts the $ placeholder back.
  const gtFill = async (m) => {
    const toRu = m.lang === "en";
    const resp = await emitAck("gtranslate", {
      text: m.sample,
      sl: GT_CODE[m.lang] || "ko",
      tl: toRu ? "ru" : "en",
    });
    if (!resp || resp.error || !resp.translation) {
      say("error", "Google Translate failed (rate-limited?).");
      return;
    }
    setEdit(m.key, toRu ? { ru: resp.translation } : { en: resp.translation });
  };

  if (!missing || !missing.length) return null;

  const f = filter.trim().toLowerCase();
  const list = f
    ? missing.filter(
        (m) => m.key.toLowerCase().includes(f) || (m.sample || "").toLowerCase().includes(f)
      )
    : missing;
  const savedCount = missing.filter((m) => edits[m.key] && edits[m.key].saved).length;

  return (
    <div className="bt-missing">
      <div className="bt-missing-head">
        <b>Untranslated terms</b>
        <span className="bt-meta">
          {missing.length} distinct, most frequent first
          {savedCount ? ` - ${savedCount} saved` : ""}
        </span>
        <input
          className="bt-missing-filter"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          placeholder="Filter..."
        />
        {onRerun && (
          <button className="bt-secondary" onClick={onRerun} disabled={rerunDisabled} title={rerunTitle}>
            {rerunLabel}
          </button>
        )}
      </div>
      <p className="bt-hint">
        Source text is shown in its lookup form: <b>$</b> stands for a number, a % or a{" "}
        <code>$s1</code>-style variable. Keep a <b>$</b> in the same spot in your translation (number
        them <code>$1</code>, <code>$2</code> in the source phrasing if the order differs). <b>GT</b>{" "}
        prefills from Google Translate (put the $ back afterwards).
      </p>
      <div className="bt-missing-wrap">
        <table className="bt-missing-grid">
          <thead>
            <tr>
              <th>#</th>
              <th>Source</th>
              <th>English</th>
              <th>Russian</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {list.map((m) => {
              const ed = edits[m.key] || {};
              const showEn = m.lang !== "en";
              const showRu = m.lang !== "ru";
              const onKey = (e) => {
                // An Enter that merely commits an IME composition must not save.
                if (e.nativeEvent && (e.nativeEvent.isComposing || e.nativeEvent.keyCode === 229)) return;
                if (e.key === "Enter") {
                  e.preventDefault();
                  save(m);
                }
              };
              return (
                <tr key={m.key} className={ed.saved ? "bt-miss-saved" : ""}>
                  <td className="bt-miss-count">{m.count}</td>
                  <td className="bt-miss-src" title={m.sample}>
                    <div className="bt-miss-key">{m.key}</div>
                    {m.sample !== m.key && <div className="bt-miss-sample">{m.sample}</div>}
                    {m.hint && (
                      <div className="bt-miss-hint">
                        {m.hint}
                        {m.hintLine && m.hintLine !== m.sample ? (
                          <div className="bt-miss-hintline">From: {m.hintLine}</div>
                        ) : null}
                      </div>
                    )}
                  </td>
                  <td>
                    {showEn ? (
                      <input
                        value={ed.en == null ? "" : ed.en}
                        onChange={(e) => setEdit(m.key, { en: e.target.value })}
                        onKeyDown={onKey}
                        placeholder="English term (with $)"
                        disabled={!!ed.saved}
                      />
                    ) : (
                      <span className="bt-miss-key">{m.key}</span>
                    )}
                  </td>
                  <td>
                    {showRu ? (
                      <input
                        value={ed.ru == null ? "" : ed.ru}
                        onChange={(e) => setEdit(m.key, { ru: e.target.value })}
                        onKeyDown={onKey}
                        placeholder={m.lang === "en" ? "Russian (with $)" : "Russian (optional)"}
                        disabled={!!ed.saved}
                      />
                    ) : (
                      <span className="bt-miss-key">{m.key}</span>
                    )}
                  </td>
                  <td className="bt-miss-actions">
                    {onPick && (
                      <button className="bt-secondary bt-mini" onClick={() => onPick(m)} title={pickTitle}>
                        {pickLabel}
                      </button>
                    )}
                    {!ed.saved && (
                      <button
                        className="bt-secondary bt-mini"
                        onClick={() => gtFill(m)}
                        title="Prefill from Google Translate"
                      >
                        GT
                      </button>
                    )}
                    {ed.saved ? (
                      <span className="bt-miss-ok">saved</span>
                    ) : (
                      <button className="bt-primary bt-mini" onClick={() => save(m)} disabled={!!ed.saving}>
                        {ed.saving ? "..." : "Save"}
                      </button>
                    )}
                    {ed.error && <div className="bt-miss-err">{ed.error}</div>}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
